import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ChevronRight,
  Check,
  User,
  CreditCard,
  FileText,
  Share2,
  Camera,
  Building2,
  Hash,
  Loader2,
  Sparkles,
  PartyPopper,
} from "lucide-react";

const BANKS = [
  "Bancolombia",
  "Davivienda",
  "BBVA",
  "Banco de Bogotá",
  "Banco de Occidente",
  "Banco Popular",
  "Banco Caja Social",
  "Banco Agrario",
  "Banco AV Villas",
  "Banco Falabella",
  "Banco Pichincha",
  "Nequi",
  "Daviplata",
  "Otro",
];

const ACCOUNT_TYPES = [
  { value: "ahorros", label: "Cuenta de Ahorros" },
  { value: "corriente", label: "Cuenta Corriente" },
  { value: "nequi", label: "Nequi" },
  { value: "daviplata", label: "Daviplata" },
];

const ONBOARDING_STEPS = [
  {
    id: "welcome",
    title: "¡Bienvenido al equipo!",
    icon: PartyPopper,
    description: "Estás a pocos pasos de comenzar a vender",
  },
  {
    id: "photo",
    title: "Foto de perfil",
    icon: Camera,
    description: "Agrega una foto para que los clientes te reconozcan",
  },
  {
    id: "bank",
    title: "Datos bancarios",
    icon: CreditCard,
    description: "Para recibir tus pagos de comisiones",
  },
  {
    id: "terms",
    title: "Términos y condiciones",
    icon: FileText,
    description: "Lee y acepta los términos del servicio",
  },
  {
    id: "catalog",
    title: "Comparte tu catálogo",
    icon: Share2,
    description: "Tu link único para empezar a vender",
  },
];

export default function VendedorBienvenida() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState({
    photo: false,
    bank: false,
    terms: false,
    catalog: false,
  });

  // Bank form state
  const [bankForm, setBankForm] = useState({
    banco: "",
    tipo_cuenta: "ahorros",
    numero_cuenta: "",
    titular_cuenta: "",
  });

  // Check if user has already completed onboarding
  useEffect(() => {
    if (user?.onboarding_completo) {
      navigate("/vendedor");
    }
    // Pre-fill bank account holder with user name
    if (user?.nombre) {
      setBankForm(prev => ({ ...prev, titular_cuenta: user.nombre }));
    }
  }, [user, navigate]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // For now, just mark as completed (photo upload can be implemented later)
    toast.success("Foto guardada (función de carga próximamente)");
    setCompleted(prev => ({ ...prev, photo: true }));
    setCurrentStep(2); // Move to bank step
  };

  const handleSkipPhoto = () => {
    setCompleted(prev => ({ ...prev, photo: true }));
    setCurrentStep(2);
  };

  const handleBankSubmit = async () => {
    if (!bankForm.banco || !bankForm.numero_cuenta || !bankForm.titular_cuenta) {
      toast.error("Completa todos los campos bancarios");
      return;
    }

    setLoading(true);
    try {
      await api.put("/usuarios/me/bank", bankForm);
      toast.success("Datos bancarios guardados");
      setCompleted(prev => ({ ...prev, bank: true }));
      setCurrentStep(3);
    } catch (error) {
      toast.error("Error al guardar datos bancarios");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTerms = async () => {
    setLoading(true);
    try {
      await api.put("/usuarios/me/terms", { accepted: true });
      toast.success("Términos aceptados");
      setCompleted(prev => ({ ...prev, terms: true }));
      setCurrentStep(4);
    } catch (error) {
      toast.error("Error al aceptar términos");
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      await api.put("/usuarios/me/onboarding", { completed: true });
      await refreshUser?.();
      toast.success("¡Onboarding completado!");
      navigate("/vendedor");
    } catch (error) {
      toast.error("Error al completar onboarding");
      navigate("/vendedor");
    } finally {
      setLoading(false);
    }
  };

  const catalogUrl = user?.catalog_code 
    ? `${window.location.origin}/catalogo/${user.catalog_code}`
    : null;

  const copyToClipboard = () => {
    if (catalogUrl) {
      navigator.clipboard.writeText(catalogUrl);
      toast.success("Link copiado al portapapeles");
      setCompleted(prev => ({ ...prev, catalog: true }));
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Welcome
        return (
          <div className="text-center space-y-6 py-8">
            <div className="w-24 h-24 bg-gradient-to-br from-[#C8902A] to-[#A67720] rounded-full flex items-center justify-center mx-auto shadow-xl shadow-[#C8902A]/30">
              <PartyPopper className="w-12 h-12 text-white" />
            </div>
            <div className="space-y-2">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#4A2C0A]">
                ¡Felicidades, {user?.nombre?.split(" ")[0]}!
              </h2>
              <p className="text-[#4A2C0A]/70 font-['Poppins']">
                Tu cuenta ha sido aprobada. Completa estos pasos rápidos para empezar a vender.
              </p>
            </div>
            <Button
              onClick={() => setCurrentStep(1)}
              className="bg-[#C8902A] hover:bg-[#A67720] text-white rounded-xl h-12 px-8 font-['Poppins']"
            >
              Comenzar
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 1: // Photo
        return (
          <div className="space-y-6 py-4">
            <div className="text-center space-y-4">
              <div className="w-32 h-32 bg-[#E5D4B3] rounded-full flex items-center justify-center mx-auto relative overflow-hidden">
                {user?.foto_url ? (
                  <img src={user.foto_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-16 h-16 text-[#4A2C0A]/40" />
                )}
              </div>
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#4A2C0A]">
                  Agrega tu foto
                </h3>
                <p className="text-sm text-[#4A2C0A]/60 font-['Poppins']">
                  Los vendedores con foto generan más confianza
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <div className="w-full h-12 bg-[#C8902A] hover:bg-[#A67720] text-white rounded-xl font-['Poppins'] flex items-center justify-center cursor-pointer transition-colors">
                  <Camera className="w-4 h-4 mr-2" />
                  Subir foto
                </div>
              </label>
              <Button
                variant="ghost"
                onClick={handleSkipPhoto}
                className="w-full text-[#4A2C0A]/60 hover:text-[#4A2C0A]"
              >
                Omitir por ahora
              </Button>
            </div>
          </div>
        );

      case 2: // Bank
        return (
          <div className="space-y-5 py-4">
            <div className="text-center">
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#4A2C0A]">
                Datos bancarios
              </h3>
              <p className="text-sm text-[#4A2C0A]/60 font-['Poppins']">
                Para recibir tus pagos de comisiones
              </p>
            </div>
            
            <div className="space-y-4 bg-white rounded-xl p-4">
              <div className="space-y-2">
                <Label>Banco</Label>
                <Select
                  value={bankForm.banco}
                  onValueChange={(v) => setBankForm({ ...bankForm, banco: v })}
                >
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue placeholder="Selecciona tu banco" />
                  </SelectTrigger>
                  <SelectContent>
                    {BANKS.map((bank) => (
                      <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tipo de cuenta</Label>
                <Select
                  value={bankForm.tipo_cuenta}
                  onValueChange={(v) => setBankForm({ ...bankForm, tipo_cuenta: v })}
                >
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Número de cuenta</Label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A2C0A]/40" />
                  <Input
                    placeholder="Número de cuenta o celular"
                    value={bankForm.numero_cuenta}
                    onChange={(e) => setBankForm({ ...bankForm, numero_cuenta: e.target.value })}
                    className="pl-9 h-11 rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Titular de la cuenta</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A2C0A]/40" />
                  <Input
                    placeholder="Nombre del titular"
                    value={bankForm.titular_cuenta}
                    onChange={(e) => setBankForm({ ...bankForm, titular_cuenta: e.target.value })}
                    className="pl-9 h-11 rounded-xl"
                  />
                </div>
              </div>
            </div>

            <Button
              onClick={handleBankSubmit}
              disabled={loading}
              className="w-full h-12 bg-[#C8902A] hover:bg-[#A67720] text-white rounded-xl font-['Poppins']"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar y continuar"}
            </Button>
          </div>
        );

      case 3: // Terms
        return (
          <div className="space-y-5 py-4">
            <div className="text-center">
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#4A2C0A]">
                Términos y Condiciones
              </h3>
            </div>
            
            <div className="bg-white rounded-xl p-4 max-h-64 overflow-y-auto text-sm text-[#4A2C0A]/80 font-['Poppins'] space-y-3">
              <p><strong>1. Relación comercial:</strong> Actúas como vendedor independiente, no como empleado.</p>
              <p><strong>2. Comisiones:</strong> Se pagan semanalmente según las ventas registradas.</p>
              <p><strong>3. Productos:</strong> Solo puedes vender productos autorizados por Torta de Cuajada.</p>
              <p><strong>4. Precios:</strong> Debes respetar los precios establecidos.</p>
              <p><strong>5. Código de conducta:</strong> Mantener un comportamiento profesional con los clientes.</p>
              <p><strong>6. Inventario:</strong> Eres responsable del inventario asignado.</p>
              <p><strong>7. Pagos:</strong> Los pagos se realizan a la cuenta bancaria registrada.</p>
            </div>

            <Button
              onClick={handleAcceptTerms}
              disabled={loading}
              className="w-full h-12 bg-[#C8902A] hover:bg-[#A67720] text-white rounded-xl font-['Poppins']"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Acepto los términos
                </>
              )}
            </Button>
          </div>
        );

      case 4: // Catalog
        return (
          <div className="space-y-6 py-4 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Sparkles className="w-10 h-10 text-green-600" />
            </div>
            <div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#4A2C0A]">
                ¡Tu catálogo está listo!
              </h3>
              <p className="text-sm text-[#4A2C0A]/60 font-['Poppins'] mt-1">
                Comparte este link con tus clientes
              </p>
            </div>

            {catalogUrl && (
              <div className="bg-[#FEF3E2] rounded-xl p-4">
                <p className="text-xs text-[#4A2C0A]/60 mb-2">Tu link de catálogo:</p>
                <p className="text-sm font-medium text-[#C8902A] break-all">{catalogUrl}</p>
                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  className="mt-3 rounded-lg border-[#C8902A] text-[#C8902A]"
                >
                  Copiar link
                </Button>
              </div>
            )}

            <Button
              onClick={handleFinish}
              disabled={loading}
              className="w-full h-12 bg-green-600 hover:bg-green-700 text-white rounded-xl font-['Poppins']"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  Ir a mi Dashboard
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FBF5E8] via-[#F5EBD7] to-[#EDD9C0] p-4">
      <div className="max-w-md mx-auto pt-8">
        {/* Progress */}
        {currentStep > 0 && (
          <div className="flex justify-center gap-2 mb-8">
            {ONBOARDING_STEPS.slice(1).map((step, index) => (
              <div
                key={step.id}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index + 1 < currentStep
                    ? "bg-green-500"
                    : index + 1 === currentStep
                    ? "bg-[#C8902A] ring-4 ring-[#C8902A]/20"
                    : "bg-[#E5D4B3]"
                }`}
              />
            ))}
          </div>
        )}

        {/* Content Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6">
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
}
